import { Injectable, OnDestroy } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class TripSignalrService implements OnDestroy {
  private static readonly BaseRetryDelayMs = 5000;
  private static readonly MaxRetryDelayMs = 60000;

  private hubConnection: signalR.HubConnection;

  // Add subjects for notifying components about SignalR events
  public userJoined = new Subject<{ tripId: string; username: string }>();
  public userLeft = new Subject<{ tripId: string; username: string }>();
  public activeUsers = new Subject<{ tripId: string; users: string[] }>();
  public userViewing = new Subject<{ tripId: string; username: string }>();
  public allActiveTrips = new Subject<{ [tripId: string]: string[] }>();
  public connectionEstablished = new Subject<void>();

  private heartbeatInterval: any;
  private currentUsername: string | null = null;
  private currentTripId: string | null = null;

  /** True while a grid is subscribed to the all-trips feed. */
  private subscribedToAllTrips = false;

  /** In-flight start() so concurrent callers share one negotiate instead of racing it. */
  private startPromise?: Promise<boolean>;

  /** The one and only pending retry. Extra reconnect chains must never be created. */
  private retryTimer?: ReturnType<typeof setTimeout>;
  private retryDelayMs = TripSignalrService.BaseRetryDelayMs;
  private destroyed = false;

  constructor() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl("/triphub")
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on("UserJoined", (tripId, username) => {
      console.log(`User ${username} joined trip ${tripId}`);
      this.userJoined.next({ tripId, username });
    });

    this.hubConnection.on("UserLeft", (tripId, username) => {
      console.log(`User ${username} left trip ${tripId}`);
      this.userLeft.next({ tripId, username });
    });

    this.hubConnection.on("ActiveUsers", (tripId, users) => {
      console.log(`Active users for trip ${tripId}:`, users);
      this.activeUsers.next({ tripId, users });
    });

    this.hubConnection.on("UserViewing", (tripId, username) => {
      console.log(`User ${username} is viewing trip ${tripId}`);
      this.userViewing.next({ tripId, username });
    });

    this.hubConnection.on("AllActiveTrips", (allTrips: { [tripId: string]: string[] }) => {
      console.log("All active trips:", allTrips);
      this.allActiveTrips.next(allTrips);
    });

    document.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this));

    // A reconnect gets a new connection id, so the trip/page membership held by the old
    // connection is gone and has to be re-established.
    this.hubConnection.onreconnected(() => {
      if (this.currentTripId && this.currentUsername) {
        this.hubConnection
          .invoke("JoinTrip", this.currentTripId, this.currentUsername)
          .catch((err) => console.error("Error rejoining trip:", err));
      }
      if (this.subscribedToAllTrips) {
        this.hubConnection
          .invoke("SubscribeToAllTrips")
          .catch((err) => console.error("Error resubscribing to all trips:", err));
      }
    });

    // Automatic reconnect gives up after a few attempts. Keep retrying (with backoff) while a
    // trip page or a grid is using the hub, so the page recovers without a refresh.
    this.hubConnection.onclose(() => {
      this.scheduleRetry();
    });

    this.start();
  }

  private start(): Promise<boolean> {
    if (this.destroyed) return Promise.resolve(false);

    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve(true);
    }
    if (this.startPromise) {
      return this.startPromise;
    }
    if (this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      // Connecting or reconnecting: let that attempt finish instead of throwing
      // "Cannot start a connection that is not in the 'Disconnected' state".
      return Promise.resolve(false);
    }

    this.startPromise = this.hubConnection
      .start()
      .then(() => {
        console.log("SignalR Connected");
        this.retryDelayMs = TripSignalrService.BaseRetryDelayMs;
        this.connectionEstablished.next();
        return true;
      })
      .catch((err) => {
        console.error("SignalR Connection Error: ", err);
        if (!this.isAuthFailure(err)) {
          this.scheduleRetry();
        }
        return false;
      })
      .finally(() => {
        this.startPromise = undefined;
      });

    return this.startPromise;
  }

  /** True while a trip page or a grid is using the trip hub. */
  private get isInUse(): boolean {
    return this.currentTripId !== null || this.subscribedToAllTrips;
  }

  private scheduleRetry(): void {
    this.clearRetry();
    if (this.destroyed || !this.isInUse) return;

    const delay = this.retryDelayMs;
    this.retryDelayMs = Math.min(delay * 2, TripSignalrService.MaxRetryDelayMs);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      void this.start();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
  }

  /**
   * A 401/403 does not fix itself, so retrying it merely spams the hub with unauthorized
   * requests. The next joinTrip() or subscribeToAllTrips() connects again.
   */
  private isAuthFailure(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err ?? "");
    return /unauthorized|forbidden|\b40[13]\b/i.test(message);
  }

  public async ensureConnection(): Promise<void> {
    await this.start();
  }

  public getConnectionState(): signalR.HubConnectionState {
    return this.hubConnection.state;
  }

  // Method to join a trip with heartbeat
  public joinTrip(tripId: string, username: string): void {
    this.currentTripId = tripId;
    this.currentUsername = username;

    // Clear any existing heartbeat interval
    this.clearHeartbeat();

    // Join the trip
    this.hubConnection
      .invoke("JoinTrip", tripId, username)
      .catch((err) => console.error("Error joining trip:", err));

    // Start the heartbeat
    this.startHeartbeat();
  }

  // Method to leave a trip
  public leaveTrip(tripId: string | null = this.currentTripId): void {
    const activeTripId = tripId ?? this.currentTripId;
    const username = this.currentUsername;

    if (activeTripId && username) {
      this.hubConnection
        .invoke("LeaveTrip", activeTripId, username)
        .catch((err) => console.error("Error leaving trip:", err));

      if (this.currentTripId === activeTripId) {
        this.clearHeartbeat();
        this.currentTripId = null;
        this.currentUsername = null;
      }
    }
  }

  // Start sending regular heartbeats
  private startHeartbeat(): void {
    // Send heartbeats every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      if (
        this.currentTripId &&
        this.currentUsername &&
        this.hubConnection.state === signalR.HubConnectionState.Connected
      ) {
        console.log("Sending heartbeat for trip:", this.currentTripId);
        this.hubConnection
          .invoke("Heartbeat", this.currentTripId, this.currentUsername)
          .catch((err) => console.error("Error sending heartbeat:", err));
      }
    }, 30000); // 30 seconds
  }

  // Clear the heartbeat interval
  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  public subscribeToAllTrips(): Promise<void> {
    this.subscribedToAllTrips = true;
    return this.ensureConnection().then(() => {
      if (this.hubConnection.state !== signalR.HubConnectionState.Connected) return;
      this.hubConnection
        .invoke("SubscribeToAllTrips")
        .catch((err) => console.error("Error subscribing to all trips:", err));
    });
  }

  public unsubscribeFromAllTrips(): void {
    this.subscribedToAllTrips = false;
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection
        .invoke("UnsubscribeFromAllTrips")
        .catch((err) => console.error("Error unsubscribing from all trips:", err));
    }
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      console.log("Tab became active, checking heartbeat and connection");

      // Check connection status and reconnect if necessary
      this.ensureConnection().then(() => {
        if (this.currentTripId && this.currentUsername) {
          // Restart the heartbeat
          this.clearHeartbeat();
          this.startHeartbeat();

          // send an immediate heartbeat
          if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
            this.hubConnection
              .invoke("Heartbeat", this.currentTripId, this.currentUsername)
              .catch((err) => console.error("Error sending heartbeat on visibility change:", err));
          }
        }
      });
    }
  }

  // Handle disconnection properly
  ngOnDestroy(): void {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange.bind(this));

    this.destroyed = true;
    this.clearRetry();
    this.leaveTrip();
    this.unsubscribeFromAllTrips();
    this.clearHeartbeat();
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  }
}
